import { useMemo, useState } from 'react'
import { useMutation, useQuery } from 'react-query'

import './InSpectionReport.style.less'
import ReportHeroPanel from './components/ReportHeroPanel/ReportHeroPanel'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { get, includes, noop, size } from 'lodash'
import {
  getGeneratedPdf,
  getInspectionComments,
  getInspectionReport,
  reportCompanySettingRef,
} from './InSpectionReport.service'
import { getCompanySetting } from '@/screens/SystemConfigure/UserManagment/UserManagment.transform'
import ReportSummaryPage from './components/ReportSummaryPage/ReportSummaryPage'
import ReportSignatures from './components/ReportSignatures/ReportSignatures'
import BackHeader from '@/layout/components/BackHeader'
import Fade from '@/components/Fade'
import { useAuth } from '@/providers/auth.provider'
import Forbidden from '@/components/Forbidden/Forbidden'
import SkeletonLoading from '@/components/SkeletonLoading/SkeletonLoading'
import { inspectionDetail } from '../Inspection/Inspection.service'
import { InspectionStatus } from '@/constant/qsf'
import { getOrderItemSetting } from '@/screens/Order/Pages/Order.transform'
import DrawerSectionComments from './components/DrawerSectionComments/DrawerSectionComments'
import { useGeneratingReportAtom } from '@/jotai/qsf.jotai'
import ReportSectionPage from './components/ReportSectionPage/ReportSectionPage'
import { INSPECTION_TYPE_SUBMIT_FOR_REVIEW } from '../Inspection/Inspection.constant'
import { PBF_QUERY_KEY } from '@/constant/pbf'
import {
  COMPANY_SETTING_FCCA_RATING,
  COMPANY_SETTING_HIDDEN_BLOCKS,
  COMPANY_SETTING_AUDIT_RATING_TITLES,
  COMPANY_SETTING_DEFECT_STATUS,
  COMPANY_SETTING_REPORT_COMBINED_DEFECTS,
} from '@/constant/user'
import { useOverallResultSettingHook } from '../Inspection/Inspection.hook'
import { getSingleTemplateRef } from '@/screens/SystemConfigure/TemplateManagement/template.service'

const {
  GET_ORDER_ITEM_SETTING,
  GET_REPORT_SETTING,
  GET_INSPECTION_COMMENTS,
  GET_REPORT_DATA,
  INSPECTION_DETAIL,
  GET_FCCA_RATING_SETTING,
  GET_HIDDEN_BLOCK_SETTING,
  GET_REPORT_COMBINED_DEFECTS_SETTING,
  GET_TEMPLATE_DETAIL_FOR_REPORT,
  GET_AUDIT_RATING_TITLES_SETTING,
} = PBF_QUERY_KEY

function InSpectionReportPrint() {
  const param: any = useParams()

  const { generatingReportAtom, setGeneratingReportAtom } =
    useGeneratingReportAtom()
  const inspectionId = param?.key
  const navigate = useNavigate()
  const [editable, seteditable] = useState(false)

  const {
    data,
    isLoading,
    error,
    refetch: refetchReport,
  } = useQuery(GET_REPORT_DATA, async () => {
    return await getInspectionReport(inspectionId)
  })
  const typeInspection = get(data, 'inspection.type', '')
  const templateId = get(data, 'inspection.template.template_id', '')
  const toEnableFetchCommentList = includes(
    [INSPECTION_TYPE_SUBMIT_FOR_REVIEW],
    typeInspection
  )

  const { data: inspectionCommentsData, isLoading: isLoadingComments } =
    useQuery(
      [GET_INSPECTION_COMMENTS, inspectionId],
      async () => {
        return await getInspectionComments(inspectionId)
      },
      { enabled: !!inspectionId && toEnableFetchCommentList }
    )

  const { data: inspection, refetch: refetchInspection } = useQuery(
    [INSPECTION_DETAIL],
    async () => {
      const data = await inspectionDetail(param?.key)
      return data?.data?.data
    }
  )
  const { data: overallResultSettingData } = useOverallResultSettingHook()

  const { user } = useAuth()

  const { data: orderItemSetting } = useQuery(
    [GET_ORDER_ITEM_SETTING, user.company.id],
    async () => {
      return await getOrderItemSetting(user.company.id ?? '')
    }
  )

  const { data: dataSetting } = useQuery([GET_REPORT_SETTING], async () => {
    return (await reportCompanySettingRef(user.company.id)).data.data
  })

  const { data: fccaRatingSettingData } = useQuery(
    [GET_FCCA_RATING_SETTING],
    async () => {
      const resp = await getCompanySetting('', COMPANY_SETTING_FCCA_RATING)
      return get(resp, 'value', [])
    }
  )

  const { data: hiddenBlockSetting } = useQuery(
    [GET_HIDDEN_BLOCK_SETTING],
    async () => {
      const resp = await getCompanySetting('', COMPANY_SETTING_HIDDEN_BLOCKS)
      return get(resp, 'value', [])
    }
  )

  const { data: combinedDefectsAqlSetting } = useQuery(
    [GET_REPORT_COMBINED_DEFECTS_SETTING],
    async () => {
      const resp = await getCompanySetting(
        '',
        COMPANY_SETTING_REPORT_COMBINED_DEFECTS
      )
      return get(resp, 'value.aql', [])
    }
  )
  const { data: templateDetail } = useQuery(
    [GET_TEMPLATE_DETAIL_FOR_REPORT, templateId],
    async () => {
      const resp = await getSingleTemplateRef(templateId)
      return resp?.data?.data
    },
    { enabled: !!templateId }
  )

  const { data: auditRatingTitleSetting } = useQuery(
    [GET_AUDIT_RATING_TITLES_SETTING, user?.company?.id],
    async () => {
      const resp = await getCompanySetting(
        '',
        COMPANY_SETTING_AUDIT_RATING_TITLES
      )
      return get(resp, 'value', null)
    }
  )

  const { data: defectStatusCompanySetting } = useQuery(
    ['GET_COMPANY_SETTING_DEFECT_STATUS'],
    async () => {
      return await getCompanySetting('defect', COMPANY_SETTING_DEFECT_STATUS)
    }
  )

  const isDefectStatusEnabled =
    Boolean(defectStatusCompanySetting?.value?.status) &&
    Boolean(templateDetail?.option?.defect_status?.status)

  // Company setting "fcca".non_compliant.status turns the Non Compliant display on;
  // otherwise the FCCA report keeps its original behaviour
  const isFccaNonCompliantEnabled =
    fccaRatingSettingData?.non_compliant?.status === true

  const fccaRatingSetting = useMemo(() => {
    const templateFccaRating = templateDetail?.option?.fcca?.rating
    if (templateFccaRating && templateFccaRating.length > 0) {
      return templateFccaRating
    }

    return fccaRatingSettingData?.rating || []
  }, [templateDetail, fccaRatingSettingData])
  const handleRefreshInspectionReport = async () => {
    await refetchInspection()
    await refetchReport()
  }

  const AUDIT_RATING_TITLES_DEFAULT = {
    rating: 'Compliance Audit Rating',
    chapter: 'Chapter',
    grading_score: 'Grading Score',
  }

  const auditRatingTitles = useMemo(() => {
    const templateTitles = templateDetail?.option?.audit_rating_titles
    const resolveTitle = (
      templateVal?: string,
      companyVal?: string,
      fallback?: string
    ) => {
      const t = templateVal?.trim()
      const c = companyVal?.trim()
      if (t) return t
      if (c) return c
      return fallback
    }
    return {
      rating: resolveTitle(
        templateTitles?.rating,
        auditRatingTitleSetting?.rating,
        AUDIT_RATING_TITLES_DEFAULT.rating
      ),
      chapter: resolveTitle(
        templateTitles?.chapter,
        auditRatingTitleSetting?.chapter,
        AUDIT_RATING_TITLES_DEFAULT.chapter
      ),
      grading_score: resolveTitle(
        templateTitles?.grading_score,
        auditRatingTitleSetting?.grading_score,
        AUDIT_RATING_TITLES_DEFAULT.grading_score
      ),
    }
  }, [templateDetail, auditRatingTitleSetting])

  function ReportPage(propsReportPage?: any) {
    const { isApproved = false } = propsReportPage || {}

    return (
      <div
        className={`wrap-body-report ${
          inspection.status != InspectionStatus.APPROVED &&
          !isApproved &&
          'background-image-draft'
        } `}
      >
        <ReportSummaryPage
          editable={editable}
          data={data}
          user={user}
          dataSetting={dataSetting}
          inspection={inspection}
          orderItemSetting={orderItemSetting}
          // ExtraSource contain any additional data
          extraSource={{
            generatingReportAtom,
            inspectionCommentsData,
            fccaRatingSetting,
            isFccaNonCompliantEnabled,
            hiddenBlockSetting,
            combinedDefectsAqlSetting,
            overallResultSettingData,
            auditRatingTitles,
          }}
        />
        <ReportSectionPage
          user={user}
          data={data.displaySections}
          templateSections={
            data?.inspection?.template?.template_value?.sections
          }
          dataInspection={inspection}
          reportType={data?.inspection?.template?.template_value?.report_type}
          editable={editable}
          isDefectStatusEnabled={isDefectStatusEnabled}
          // ExtraSource contain any additional data
          extraSource={{ generatingReportAtom, inspectionCommentsData }}
        />
        <ReportSignatures data={data.signatures} />
      </div>
    )
  }

  const exportPdfMutation = useMutation(async (pdfOptions?: any) => {
    await getGeneratedPdf(inspectionId, pdfOptions)
    setGeneratingReportAtom({ isGenerating: false })
  })

  const onDownloadPDF = (options?: any) => {
    seteditable(false)
    setTimeout(() => {
      exportPdfMutation.mutateAsync(options)
    }, 100)
  }

  const location = useLocation()
  const onGoBack = () => {
    if (location.key !== 'default') {
      navigate(-1)
    } else {
      navigate('/qsf')
    }
  }

  if ((error as any)?.response.status == 403)
    return (
      <>
        <BackHeader onClick={() => onGoBack()} title={'Back'} />
        <Forbidden />
      </>
    )

  if (!size(data) || !size(inspection)) {
    return <></>
  }

  return (
    <>
      <BackHeader onClick={onGoBack} title={'Back'} />
      <div className="report-container">
        <div className="sticky-header">
          <ReportHeroPanel
            inspection={inspection}
            editable={editable}
            refrechInspection={handleRefreshInspectionReport}
            onToggleEdit={seteditable}
            onClickPDF={() => {
              setGeneratingReportAtom({ isGenerating: true })
              onDownloadPDF()
            }}
            onClickPDFWithComment={() => {
              if (isLoadingComments) return

              setGeneratingReportAtom({ isGenerating: true })
              onDownloadPDF({ comments_included: true })
            }}
            onClickExcel={noop}
            loadingExcel={false}
            ReportPage={ReportPage}
            loadingPdf={exportPdfMutation.isLoading}
          />
          <div className="fake-gap" />
        </div>
        {isLoading ? (
          <div>
            <SkeletonLoading />
            <SkeletonLoading />
          </div>
        ) : (
          <Fade>
            <div
              style={{
                backgroundColor: 'white',
                padding: '20px',
                borderRadius: 10,
              }}
            >
              {ReportPage()}
            </div>
          </Fade>
        )}
      </div>

      <DrawerSectionComments
        sectionsData={data?.displaySections}
        dataInspection={data?.inspection}
        handleRefreshReport={handleRefreshInspectionReport}
      />
    </>
  )
}

export default InSpectionReportPrint
